import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  Report,
  ReportField,
  ReportLevelType,
} from '../../../models/api/reports/report';
import { ExportService } from '../../../services/export/export.service';
import {
  ReportLevelService,
  ReserveStatusType,
} from '../../../services/reports/report-level.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ReportFieldExportMapper } from '../../../utilities/report-field-export-mapper';
import { ReportFieldUtils } from '../../../utilities/report-field-utils';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ConfirmDialogComponent } from '../../core/dialogs/confirm-dialog.component';
import { DualListPickerComponent } from './dual-list-picker.component';

@Component({
  selector: 'app-dual-list-fields',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DualListPickerComponent, ConfirmDialogComponent],
  template: `
    <div>
      @if (showXlsWarning()) {
        <div
          class="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-px-3 tw-py-2 tw-rounded tw-bg-[var(--sed-purple-40-color)] tw-text-[var(--sed-engaged-black-color)] tw-font-bold"
        >
          <i class="fa-circle-info fa-light"></i>
          <span>{{ xlsWarningMessage() }}</span>
        </div>
      }

      @if (showPendingReservesNotification()) {
        <div
          class="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-px-3 tw-py-2 tw-rounded tw-bg-[var(--sed-purple-40-color)] tw-text-[var(--sed-engaged-black-color)] tw-font-bold"
        >
          <i class="fa-circle-info fa-light"></i>
          <span
            >Pending reserve records are changes to the reserve amount on a
            claim that are awaiting approval. These types of records are subject
            to change while they are in pending status, and will ultimately be
            accepted or rejected. A report of Pending Reserves will provide the
            current information for pending reserve records.</span
          >
        </div>
      }

      <app-dual-list-picker
        [source]="availableFields()"
        [target]="selectedFields()"
        [loading]="loadingFields()"
        [maxSelections]="maxFields()"
        [availableWidth]="450"
        [selectedWidth]="450"
        [showRestoreDefaults]="true"
        [showExport]="true"
        [disabled]="isAdHocPickerDisabled()"
        [showLevelDropdown]="showLevelDropdown()"
        [levelDropdownLabel]="levelDropdownLabel()"
        [levelOptions]="levelOptions()"
        [selectedLevel]="selectedLevel()"
        [itemFilter]="levelFilter()"
        [isMandatoryField]="mandatoryFieldCheck()"
        [config]="pickerConfig()"
        [showButtons]="{ transfer: true, reorder: true }"
        (filter)="handleFilterChange($event)"
        (targetChange)="handleTargetChange($event)"
        (restoreDefaults)="handleRestoreDefaults()"
        (exportClicked)="onExport($event)"
        (levelChange)="handleLevelChange($event)"
      />

      <app-confirm-dialog
        #levelChangeDialog
        label="Notification"
        [message]="levelChangeDialogMessage()"
        yesLabel="OK"
        noLabel="Cancel"
        (confirm)="confirmLevelChange()"
        (cancel)="cancelLevelChange()"
      />
    </div>
  `,
})
export class DualListFieldsComponent {
  report = input.required<Report>();

  fieldsChanged = output<void>();

  private reports = inject(ReportsService);
  private snackbar = inject(Snackbar);
  private exportService = inject(ExportService);
  private cdr = inject(ChangeDetectorRef);
  reportLevelService = inject(ReportLevelService);

  @ViewChild('levelChangeDialog') levelChangeDialog: ConfirmDialogComponent;

  availableFields = signal<ReportField[]>([]);
  selectedFields = signal<ReportField[]>([]);
  loadingFields = signal(true);
  private initialSearchInProgress = false;

  selectedLevel = signal<ReportLevelType | ReserveStatusType>('N');
  pendingLevel = signal<ReportLevelType | ReserveStatusType | null>(null);

  invalid = computed(() => {
    if (this.loadingFields()) return false;
    return this.selectedFields().length === 0;
  });

  maxFields = computed(() => this.report().reportRequest?.maxReportFields);

  isAdHocPaymentReport = computed(() => {
    const reportId = this.report().reportTemplate?.id;
    return reportId
      ? this.reportLevelService.isAdHocPaymentReport(reportId)
      : false;
  });

  isAdHocReserveReport = computed(() => {
    const reportId = this.report().reportTemplate?.id;
    return reportId
      ? this.reportLevelService.isAdHocReserveReport(reportId)
      : false;
  });

  isAdHocClaimReport = computed(() => {
    const reportId = this.report().reportTemplate?.id;
    return reportId
      ? this.reportLevelService.isAdHocClaimReport(reportId)
      : false;
  });

  // Payment, Reserve, and Claim adhoc reports
  isAdHocReport = computed(() => {
    return (
      this.isAdHocPaymentReport() ||
      this.isAdHocReserveReport() ||
      this.isAdHocClaimReport()
    );
  });

  // Disabled when no level/status is selected (only for Payment/Reserve which require a level)
  isAdHocPickerDisabled = computed(() => {
    return (
      (this.isAdHocPaymentReport() || this.isAdHocReserveReport()) &&
      this.selectedLevel() === 'N'
    );
  });

  showLevelDropdown = computed(() => {
    return this.isAdHocPaymentReport() || this.isAdHocReserveReport();
  });

  levelDropdownLabel = computed(() => {
    return this.isAdHocReserveReport() ? 'Reserve Status' : 'Report Level';
  });

  levelOptions = computed(() => {
    return this.isAdHocReserveReport()
      ? this.reportLevelService.reserveStatusOptions
      : this.reportLevelService.levelOptions;
  });

  levelChangeDialogMessage = computed(() => {
    return this.isAdHocReserveReport()
      ? 'Changing the Reserve Status will remove any fields from the Selected box that do not apply to the new Reserve Status.'
      : 'Changing the Report Level will remove any fields from the Selected box that do not apply to the new Report Level.';
  });

  showPendingReservesNotification = computed(() => {
    return (
      this.isAdHocReserveReport() && this.selectedLevel() === 'PENDING_RESERVES'
    );
  });

  levelFilter = computed(() => {
    const level = this.selectedLevel();

    if (this.isAdHocPaymentReport()) {
      return this.reportLevelService.createLevelFilter(
        level as ReportLevelType,
      );
    }

    if (this.isAdHocReserveReport()) {
      return (field: ReportField) =>
        this.reportLevelService.isReserveFieldVisibleForStatus(
          field,
          level as ReserveStatusType,
        );
    }

    return null;
  });

  mandatoryFieldCheck = computed(() => {
    const level = this.selectedLevel();

    if (this.isAdHocPaymentReport()) {
      return (field: ReportField) =>
        this.reportLevelService.isFieldMandatory(
          field,
          level as ReportLevelType,
        );
    }

    if (this.isAdHocReserveReport()) {
      return (field: ReportField) =>
        this.reportLevelService.isReserveFieldMandatory(
          field,
          level as ReserveStatusType,
        );
    }

    return null;
  });

  pickerConfig = computed(() => {
    const isAdHoc = this.isAdHocReport();
    const baseConfig = {
      sourceFilterLabel: 'Filter available items',
      targetFilterLabel: 'Filter selected items',
      recordLabel: 'item',
      sourceEmptyLabel: 'Type to search',
      sourceEmptyMessage: 'Enter at least 3 characters to find fields',
      targetEmptyLabel: 'No fields selected',
      targetEmptyMessage: 'Click fields from the left to add them here',
      hideSourceEmptyState: isAdHoc,
      showHeaderMessage: !isAdHoc,
    };

    if (this.isAdHocPickerDisabled()) {
      const disabledMessage = this.isAdHocReserveReport()
        ? 'Please select a reserve status to enable field selection'
        : 'Please select a report level to enable field selection';
      return {
        ...baseConfig,
        disabledMessage,
        disabledIcon: 'fa-light fa-lock',
      };
    }

    return baseConfig;
  });

  showXlsWarning = computed(() => {
    const selectedFieldsCount = this.selectedFields().length;
    const sortFieldsCount =
      this.report().reportRequest?.sortBreakOptions?.length ?? 0;
    return selectedFieldsCount + sortFieldsCount - 1 > 256;
  });

  xlsWarningMessage = computed(
    () =>
      'If more than 256 fields are selected, including Sort fields, the XLS format will be disabled.',
  );

  constructor() {
    effect(() => {
      const currentReport = this.report();
      this.initializeFields(currentReport);
    });
  }

  private initializeFields(report: Report): void {
    if (report.reportRequest?.reportLevel) {
      this.selectedLevel.set(report.reportRequest.reportLevel);
    } else {
      this.selectedLevel.set('N');
    }

    if (
      this.isAdHocClaimReport() &&
      report.reportRequest &&
      report.reportRequest.reportLevel !== 'N'
    ) {
      this.reports.currentReport.update((r) => ({
        ...r,
        reportRequest: {
          ...r.reportRequest!,
          reportLevel: 'N' as ReportLevelType,
        },
      }));
    }

    if (
      report.reportRequest?.reportFields &&
      report.reportRequest.reportFields.length > 0
    ) {
      let fields = report.reportRequest.reportFields;
      if (report.reportSections?.hasCustomFinancials) {
        fields = fields.filter((f) => f.inlineFilterType !== 'COMP_FINANCIAL');
      }
      if (report.reportSections?.hasCustomNonFinancials) {
        fields = fields.filter(
          (f) => f.inlineFilterType !== 'COMP_NON_FINANCIAL',
        );
      }

      fields = fields.filter((f) => !f.columnName?.includes('NULL_'));

      fields = fields.map((f) =>
        f.selectedFieldLabel ? { ...f, defaultValue: f.selectedFieldLabel } : f,
      );

      const sorted = ReportFieldUtils.sortByUserOrder(fields);
      this.selectedFields.set(sorted);
    }

    this.performInitialSearch();
  }

  handleLevelChange(newLevel: string): void {
    const newLevelTyped = newLevel as ReportLevelType;

    if (this.selectedFields().length > 0) {
      this.pendingLevel.set(newLevelTyped);
      this.levelChangeDialog.show();
    } else {
      this.applyLevelChange(newLevelTyped);
    }
  }

  confirmLevelChange(): void {
    const newLevel = this.pendingLevel();
    if (newLevel) {
      this.applyLevelChange(newLevel);
      this.pendingLevel.set(null);
    }
  }

  cancelLevelChange(): void {
    const currentLevel = this.selectedLevel();
    this.pendingLevel.set(null);

    this.selectedLevel.set('_RESET_' as ReportLevelType);
    setTimeout(() => {
      this.selectedLevel.set(currentLevel);
    }, 0);
  }

  private async applyLevelChange(
    newLevel: ReportLevelType | ReserveStatusType,
  ): Promise<void> {
    let compatibleFields: ReportField[];

    if (this.isAdHocReserveReport()) {
      compatibleFields =
        this.reportLevelService.filterIncompatibleReserveFields(
          this.selectedFields(),
          newLevel as ReserveStatusType,
        );
    } else {
      compatibleFields = this.reportLevelService.filterIncompatibleFields(
        this.selectedFields(),
        newLevel as ReportLevelType,
      );
    }

    this.selectedLevel.set(newLevel as ReportLevelType);
    this.selectedFields.set(compatibleFields);

    // Old app: always add defaults for the new level (additive)
    if (newLevel !== 'N') {
      if (this.isAdHocReserveReport()) {
        await this.loadDefaultFieldsForReserveStatus(
          newLevel as ReserveStatusType,
        );
      } else {
        await this.loadDefaultFieldsForLevel(newLevel as ReportLevelType);
      }
    }

    this.reports.currentReport.update((report: Report) => {
      if (report.reportRequest) {
        report.reportRequest.reportLevel = newLevel as ReportLevelType;
        report.reportRequest.reportFields = [...this.selectedFields()];
      }
      return report;
    });

    this.fieldsChanged.emit();
  }

  private async loadDefaultFieldsForLevel(
    level: ReportLevelType,
  ): Promise<void> {
    const report = this.report();
    const reportTemplate = report.reportTemplate;
    if (!reportTemplate) return;

    this.loadingFields.set(true);
    await new Promise((resolve) =>
      requestAnimationFrame(() => resolve(undefined)),
    );

    try {
      const isDyo = reportTemplate.title?.toLowerCase().includes('design') ?? false;
      const allFields = await this.reports.searchReportFields(
        'JURIS',
        reportTemplate.model,
        reportTemplate.id,
        report.reportInfo.contractNumber,
        '%',
        1,
        isDyo,
      );

      const defaultFields = this.reportLevelService.getDefaultFieldsForLevel(
        allFields,
        level,
      );

      const currentSelected = this.selectedFields();
      const selectedKeySet = new Set(
        currentSelected.map((f) => this.reportLevelService.getFieldKey(f)),
      );

      const newDefaultFields = defaultFields.filter(
        (f) => !selectedKeySet.has(this.reportLevelService.getFieldKey(f)),
      );

      if (newDefaultFields.length > 0) {
        const updatedSelected = [...currentSelected, ...newDefaultFields];
        const withOrder = ReportFieldUtils.addUserInputOrder(updatedSelected);
        this.selectedFields.set(withOrder);
      }

      const filtered = ReportFieldUtils.filterSelectedFields(
        allFields,
        this.selectedFields(),
      );
      this.availableFields.set(filtered);
    } finally {
      this.loadingFields.set(false);
      this.cdr.markForCheck();
    }
  }

  private async loadDefaultFieldsForReserveStatus(
    status: ReserveStatusType,
  ): Promise<void> {
    const report = this.report();
    const reportTemplate = report.reportTemplate;
    if (!reportTemplate) return;

    this.loadingFields.set(true);
    await new Promise((resolve) =>
      requestAnimationFrame(() => resolve(undefined)),
    );

    try {
      const isDyo = reportTemplate.title?.toLowerCase().includes('design') ?? false;
      const allFields = await this.reports.searchReportFields(
        'JURIS',
        reportTemplate.model,
        reportTemplate.id,
        report.reportInfo.contractNumber,
        '%',
        1,
        isDyo,
      );

      const defaultFields =
        this.reportLevelService.getReserveDefaultFieldsForStatus(
          allFields,
          status,
        );

      const currentSelected = this.selectedFields();
      const selectedNameSet = new Set(
        currentSelected.map((f) => f.defaultValue || ''),
      );

      const newDefaultFields = defaultFields.filter(
        (f) => !selectedNameSet.has(f.defaultValue || ''),
      );

      if (newDefaultFields.length > 0) {
        const updatedSelected = [...currentSelected, ...newDefaultFields];
        const withOrder = ReportFieldUtils.addUserInputOrder(updatedSelected);
        this.selectedFields.set(withOrder);
      }

      const filtered = ReportFieldUtils.filterSelectedFields(
        allFields,
        this.selectedFields(),
      );
      this.availableFields.set(filtered);
    } finally {
      this.loadingFields.set(false);
      this.cdr.markForCheck();
    }
  }

  private async performInitialSearch(): Promise<void> {
    this.initialSearchInProgress = true;
    this.loadingFields.set(true);
    await new Promise((resolve) =>
      requestAnimationFrame(() => resolve(undefined)),
    );

    const report = this.report();
    const reportTemplate = report.reportTemplate;
    if (!reportTemplate) {
      this.initialSearchInProgress = false;
      this.loadingFields.set(false);
      return;
    }

    try {
      const isDyo = reportTemplate.title?.toLowerCase().includes('design') ?? false;
      const allFields = await this.reports.searchReportFields(
        'JURIS',
        reportTemplate.model,
        reportTemplate.id,
        report.reportInfo.contractNumber,
        '%',
        1,
        isDyo,
      );

      this.enrichSelectedFields(allFields);

      const filtered = ReportFieldUtils.filterSelectedFields(
        allFields,
        this.selectedFields(),
      );

      this.availableFields.set(filtered);
    } finally {
      this.initialSearchInProgress = false;
      this.loadingFields.set(false);
      this.cdr.markForCheck();
    }
  }

  async handleFilterChange(searchTerm: string): Promise<void> {
    if (this.initialSearchInProgress) return;

    if (searchTerm.length === 0) {
      await this.performInitialSearch();
      return;
    }

    try {
      this.loadingFields.set(true);
      await new Promise((resolve) =>
        requestAnimationFrame(() => resolve(undefined)),
      );

      const report = this.report();
      const reportTemplate = report.reportTemplate;

      if (!reportTemplate) {
        this.loadingFields.set(false);
        return;
      }

      const isDyo = reportTemplate.title?.toLowerCase().includes('design') ?? false;
      const allFields = await this.reports.searchReportFields(
        'JURIS',
        reportTemplate.model,
        reportTemplate.id,
        report.reportInfo.contractNumber,
        searchTerm,
        1,
        isDyo,
      );

      const filtered = ReportFieldUtils.filterSelectedFields(
        allFields,
        this.selectedFields(),
      );

      this.availableFields.set(filtered);
    } finally {
      this.loadingFields.set(false);
      this.cdr.markForCheck();
    }
  }

  handleTargetChange(target: ReportField[]): void {
    const withOrder = ReportFieldUtils.addUserInputOrder(target);
    this.selectedFields.set(withOrder);
    this.updateReport();
  }

  async handleRestoreDefaults(): Promise<void> {
    this.selectedLevel.set('N');
    this.selectedFields.set([]);
    this.updateReport();
    await this.performInitialSearch();
  }

  private enrichSelectedFields(allFields: ReportField[]): void {
    const fieldMap = new Map(
      allFields.map((f) => [`${f.tableAlias}::${f.columnName}`, f]),
    );

    const current = this.selectedFields();
    let changed = false;

    const updated = current.map((field) => {
      const match = fieldMap.get(`${field.tableAlias}::${field.columnName}`);
      if (match && match.isClientField !== field.isClientField) {
        changed = true;
        return { ...field, isClientField: match.isClientField };
      }
      return field;
    });

    if (changed) {
      this.selectedFields.set(updated);
    }
  }

  private updateReport(): void {
    this.reports.currentReport.update((report: Report) => {
      if (report.reportRequest) {
        report.reportRequest.reportFields = [...this.selectedFields()];
        report.reportRequest.reportLevel =
          this.selectedLevel() as ReportLevelType;
      }
      return report;
    });
    this.fieldsChanged.emit();
  }

  async onExport(format: ExportFormat): Promise<void> {
    const report = this.report();
    const reportTemplate = report.reportTemplate;
    if (!reportTemplate) return;

    const isDyo = reportTemplate.title?.toLowerCase().includes('design') ?? false;
    const allFields = await this.reports.searchReportFields(
      'JURIS',
      reportTemplate.model,
      reportTemplate.id,
      report.reportInfo?.contractNumber,
      '%',
      1,
      isDyo,
    );

    const exportFields = allFields
      .filter((f) => f.defaultValue)
      .sort((a, b) => a.defaultValue.localeCompare(b.defaultValue));

    const reportName = reportTemplate.title ?? 'Report';
    const clientName =
      report.reportInfo?.clientName || report.reportInfo?.contractNumber || '';

    if (exportFields.length === 0) {
      this.snackbar.show('No fields available to export', 'Info');
      return;
    }

    if (format === 'xlsx') {
      const excelData = ReportFieldExportMapper.toExcelExport(
        exportFields,
        reportName,
        clientName,
      );
      await this.exportService.exportExcel(excelData, excelData.fileName);
    } else {
      const csvData = ReportFieldExportMapper.toCsvExport(
        exportFields,
        reportName,
        clientName,
      );
      await this.exportService.exportCsv(csvData, csvData.fileName);
    }
  }
}
